import React, { useState, useEffect } from 'react';
import PageTabs from '../shared/PageTabs';
import { SETTINGS_TABS } from '../shared/tabConfigs';
import { Box, Typography, Switch, Button, TextField, FormControlLabel, Divider, Paper, Grid, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { settingsService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import ConfirmDialog from '../common/ConfirmDialog';
import LoadingSkeleton from '../common/LoadingSkeleton';
const FeedbackSettings = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [enableFeedback, setEnableFeedback] = useState(false);
  const [mandatoryQuestions, setMandatoryQuestions] = useState([]);
  const [generalQuestions, setGeneralQuestions] = useState([]);
  const [classroomQuestions, setClassroomQuestions] = useState([]);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  useEffect(() => {
    fetchFeedbackSettings();
  }, []);

  const fetchFeedbackSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsService.getFeedbackSettings();
      if (response.data.success) {
        const { settings, mandatoryQuestions, generalQuestions, classroomQuestions } = response.data;

        setEnableFeedback(settings.value === '1' || settings.value === 1);
        setMandatoryQuestions(mandatoryQuestions || []);
        setGeneralQuestions(generalQuestions || []);
        setClassroomQuestions(classroomQuestions || []);
      } else {
        showToast('Failed to load feedback settings: Invalid response format', 'error');
      }
    } catch (error) {
      showToast('Failed to load feedback settings', 'error');
      console.error('Error loading feedback settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const formData = {
        enableFeedback,
        questions: [
          ...generalQuestions.map(q => ({ ...q, question_type: 'general' })),
          ...classroomQuestions.map(q => ({ ...q, question_type: 'classroom' }))
        ],
        deletedQuestionIds
      };
      const response = await settingsService.updateFeedbackSettings(formData);
      if (response.data.success || response.data.status === 'ok') {
        showToast('Feedback settings saved successfully', 'success');
        setDeletedQuestionIds([]);
      }
      else {
        showToast(response.data.message || 'Failed to save settings', 'error');
      }
    } catch (error) {
      showToast('Failed to save feedback settings', 'error');
      console.error('Error saving feedback settings:', error);
    }
  };

const addQuestion = (type) => {
  if (type === 'general') {
    if (generalQuestions.length >= 5) {
      showToast('You can only add up to 5 Course Feedback Questions', 'warning');
      return;
    }
    const newQuestion = {
      question: '',
      is_enabled: true,
      question_type: type
    };
    setGeneralQuestions([...generalQuestions, newQuestion]);
  } else {
    if (classroomQuestions.length >= 5) {
      showToast('You can only add up to 5 Classroom Questions', 'warning');
      return;
    }
    const newQuestion = {
      question: '',
      is_enabled: true,
      question_type: type
    };
    setClassroomQuestions([...classroomQuestions, newQuestion]);
  }
};


  const handleQuestionChange = (index, value, type) => {
    if (type === 'general') {
      const updatedQuestions = [...generalQuestions];
      updatedQuestions[index].question = value;
      setGeneralQuestions(updatedQuestions);
    } else {
      const updatedQuestions = [...classroomQuestions];
      updatedQuestions[index].question = value;
      setClassroomQuestions(updatedQuestions);
    }
  };

  const handleToggleChange = (index, value, type) => {
    if (type === 'general') {
      const updatedQuestions = [...generalQuestions];
      updatedQuestions[index].is_enabled = value;
      setGeneralQuestions(updatedQuestions);
    } else {
      const updatedQuestions = [...classroomQuestions];
      updatedQuestions[index].is_enabled = value;
      setClassroomQuestions(updatedQuestions);
    }
  };

  const handleDeleteClick = (index, type) => {
    setQuestionToDelete({ index, type });
    setConfirmDialogOpen(true);
  };
  const confirmDelete = () => {
    const { index, type } = questionToDelete;

    if (type === 'general') {
      const question = generalQuestions[index];
      if (question.id) {
        setDeletedQuestionIds([...deletedQuestionIds, question.id]);
      }
      const updatedQuestions = generalQuestions.filter((_, i) => i !== index);
      setGeneralQuestions(updatedQuestions);
    } else {
      const question = classroomQuestions[index];
      if (question.id) {
        setDeletedQuestionIds([...deletedQuestionIds, question.id]);
      }
      const updatedQuestions = classroomQuestions.filter((_, i) => i !== index);
      setClassroomQuestions(updatedQuestions);
    }

    // Close the dialog
    setConfirmDialogOpen(false);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
    setQuestionToDelete(null);
  };

  if (loading) {
    return <LoadingSkeleton rows={5} />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <PageTabs tabs={SETTINGS_TABS} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" gutterBottom className='page-title'>Feedback Settings</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveSettings}
        >
          Save Changes
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, pt: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={enableFeedback}
              onChange={(e) => setEnableFeedback(e.target.checked)}
              color="primary"
            />
          }
          label="Enable Feedback for newly created Courses and Learning Paths."
        />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          This question will show once Learners complete a Course or Learning Path. Questions should be worded as statements that Learners would then respond to on a scale of 1-5.
        </Typography>
      </Paper>

      {/* {mandatoryQuestions.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Mandatory Questions</Typography>
          <Divider sx={{ mb: 2, mt: 1 }} />
          {mandatoryQuestions.map((question, index) => (
            <Typography key={index} variant="body1" sx={{ mb: 1, fontSize: '0.875rem' }}>
              {question.question}
            </Typography>
          ))}
        </Paper>
      )} */}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Feedback Questions </Typography>
        <Divider sx={{ mb: 2, mt: 1 }} />

        {generalQuestions.map((question, index) => (
          <Grid container spacing={2} key={index} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Question"
                value={question.question}
                onChange={(e) => handleQuestionChange(index, e.target.value, 'general')}
                required
                InputProps={{
                  sx: {
                    fontSize: '0.875rem',
                    minHeight: '10px'
                  },
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={question.is_enabled}
                    onChange={(e) => handleToggleChange(index, e.target.checked, 'general')}
                    color="primary"
                  />
                }
                label="Enabled"
              />
            </Grid>
            <Grid item xs={2}>
              {!question.is_default && (
                <IconButton
                  color="error"
                  onClick={() => handleDeleteClick(index, 'general')}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        ))}
        {/* show button only if less than 5 questions. One outlined style (K2.3 pt 15). Removed 3 Nov 2026 (K2.3 pt 15): a text button. */}
        {generalQuestions.length < 5 && (
          <Button 
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addQuestion('general')}
            sx={{ mt: 1 }}
          >
            Add Question
          </Button>
        )}
      </Paper>

      {/* <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Classroom Questions</Typography>
        <Divider sx={{ mb: 2, mt: 1 }} />

        {classroomQuestions.map((question, index) => (
          <Grid container spacing={2} key={index} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={8}>
              <TextField
                fullWidth
                label="Question"
                value={question.question}
                onChange={(e) => handleQuestionChange(index, e.target.value, 'classroom')}
                required
                InputProps={{
                  sx: {
                    fontSize: '0.875rem',
                    minHeight: '10px'
                  },
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={question.is_enabled}
                    onChange={(e) => handleToggleChange(index, e.target.checked, 'classroom')}
                    color="primary"
                  />
                }
                label="Enabled"
              />
            </Grid>
            <Grid item xs={2}>
              {!question.is_default && (
                <IconButton
                  color="error"
                  onClick={() => handleDeleteClick(index, 'classroom')}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={() => addQuestion('classroom')}
          sx={{ mt: 1 }}
        >
          Add Question
        </Button>
      </Paper> */}

      <ConfirmDialog
        open={confirmDialogOpen}
        title="Confirm Deletion"
        content="Are you sure you want to delete this question? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </Box>
  );
};
  
export default FeedbackSettings;